import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { formatDateTime } from "@livecommerce/ui";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { ApiError } from "../api/client";
import { useMayRunShows } from "../auth/roles";
import { type Show, type ShowState, shows } from "./api";
import { formatDuration, showDuration } from "./duration";
import styles from "./Shows.module.scss";

/**
 * Every show, and the form that makes a new one (spec §8, §17.2).
 *
 * The list is unfiltered and unpaginated, which matches what `listShows` answers and what a shop
 * running a handful of shows a week needs. Sorting is the API's: newest first, by when each show
 * was created — not by its start time, which is optional and often wrong.
 */
export function ShowsPage() {
  const { t } = useTranslation();
  const mayRun = useMayRunShows();
  const [list, setList] = useState<Show[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const ticking = list?.some((show) => show.state === "live") ?? false;
  const now = useNow(ticking);

  /**
   * One lookup's outcome, applied in one place — and the reason the effect below uses `.then`
   * rather than awaiting.
   *
   * React's `set-state-in-effect` rule rejects an effect that reaches `setState` synchronously
   * and accepts one that does so from a callback when an external system answers, which is
   * exactly what this is. `useSession` is written the same way and for the same reason.
   */
  const apply = useCallback(
    (next: Show[] | undefined, failure: unknown) => {
      if (next) setList(next);
      setError(
        failure === undefined
          ? undefined
          : failure instanceof ApiError
            ? failure.message
            : t("shows.unreachable"),
      );
    },
    [t],
  );

  /**
   * The list is read on mount and never again from here.
   *
   * Not even after a show is created, which is the only event this page has: creating one
   * navigates to it, so the list is unmounted before a refresh could land — and returning to this
   * page mounts it afresh. The live-duration ticker below is the only thing that still
   * updates anything here, and it updates a clock rather than the data.
   */
  useEffect(() => {
    // StrictMode mounts every effect twice in development; without `live` the first mount's
    // response resolves against an unmounted tree.
    let live = true;
    void shows.list().then(
      (result) => {
        if (live) apply(result.shows, undefined);
      },
      (failure: unknown) => {
        if (live) apply(undefined, failure);
      },
    );
    return () => {
      live = false;
    };
  }, [apply]);

  return (
    <Stack gap="lg">
      {/* Creating a show is the host's (spec §3). A moderator gets the list, which is how they
          reach a show's comments — `POST /api/shows` answers them 403. */}
      {mayRun ? <NewShow /> : null}

      {error ? (
        <Alert color="red" variant="light" radius="md">
          {error}
        </Alert>
      ) : null}

      {list === undefined ? (
        <Group justify="center" py="xl">
          <Loader size="sm" color="ink" />
        </Group>
      ) : list.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("shows.empty")}
        </Text>
      ) : (
        <Stack gap="xs">
          {list.map((show) => (
            <ShowRow key={show.id} show={show} now={now} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * A show starts as a draft with a title and, usually, an intended start time (spec §8).
 *
 * The start time is optional and advisory in both directions: a show is often created before
 * anyone has agreed when it runs, and even once set it is the host going live that starts the
 * show, never the clock (spec §8.1).
 *
 * **Creating one navigates straight to it**, rather than adding a row to the list behind the form.
 * Nobody creates a show in order to look at a list of shows: the next thing is always building it
 * — adding lots, copying the share link `R-HOST-33` has just issued — and all of that is on the
 * show's own page. Landing there also means the address bar holds the show a host is working on,
 * so it can be bookmarked, or opened in a second tab beside another one (`R-HOST-31` expects two
 * people running one show).
 */
function NewShow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function create() {
    setBusy(true);
    setError(undefined);
    try {
      // `datetime-local` gives a wall-clock string with no zone; `new Date` reads it in the
      // browser's own zone, which is what the host meant when they typed it. Everything is stored
      // in UTC (arch §10) and this is where the conversion belongs.
      const created = await shows.create(
        title.trim(),
        when ? new Date(when).toISOString() : null,
      );
      // No `setBusy(false)` and no clearing the fields on this path: the component is on its way
      // out, and a button that stops spinning before it unmounts reads as ready to be pressed
      // again — which is how a host ends up with two shows called the same thing.
      void navigate(`/shows/${created.show.id}`);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t("shows.unreachable"));
      setBusy(false);
    }
  }

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="sm">
        <Text size="sm" fw={600}>
          {t("shows.newHeading")}
        </Text>
        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput
            className={styles.grow}
            label={t("shows.titleLabel")}
            value={title}
            maxLength={120}
            onChange={(event) => setTitle(event.currentTarget.value)}
            disabled={busy}
          />
          <TextInput
            type="datetime-local"
            label={t("shows.whenLabel")}
            value={when}
            onChange={(event) => setWhen(event.currentTarget.value)}
            disabled={busy}
          />
          <Button onClick={() => void create()} loading={busy} disabled={!title.trim()}>
            {t("shows.create")}
          </Button>
        </Group>
        {error ? (
          <Text size="xs" c="red">
            {error}
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
}

/** Spec §8's five states, coloured so the live one is findable without reading. */
const STATE_COLOUR: Record<ShowState, string> = {
  draft: "gray",
  scheduled: "blue",
  live: "red",
  ended: "ink",
  cancelled: "gray",
};

export function ShowStateBadge({ state }: { state: ShowState }) {
  const { t } = useTranslation();
  return (
    <Badge color={STATE_COLOUR[state]} variant="light" radius="sm">
      {t(`shows.state.${state}`)}
    </Badge>
  );
}

/**
 * A clock that only runs when something is watching it.
 *
 * A live show's duration has to tick, and a list of ended shows has nothing to tick — so the
 * interval is created only while at least one show is live, and torn down when none is. A panel
 * left open overnight on a finished show should not be re-rendering once a second until morning.
 */
function useNow(ticking: boolean): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!ticking) return;
    // Set from the callback rather than in the effect body, which is React's
    // `set-state-in-effect` rule — see `useSession` for the same shape and the reasoning.
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [ticking]);

  return now;
}

function ShowRow({ show, now }: { show: Show; now: number }) {
  const { t } = useTranslation();
  const duration = showDuration(show, now);
  return (
    <Paper
      component={Link}
      to={`/shows/${show.id}`}
      withBorder
      radius="md"
      p="md"
      className={styles.row}
    >
      <Group justify="space-between" wrap="nowrap" align="center">
        <Stack gap={2}>
          <Text fw={600} size="sm">
            {show.title}
          </Text>
          <Text size="xs" c="dimmed">
            {show.scheduledStartAt
              ? formatDateTime(new Date(show.scheduledStartAt))
              : t("shows.noStartTime")}
            {/* Only once a show has actually started. A scheduled show has a start time, which
                is a different thing and is already on the line. */}
            {duration === null ? null : (
              <>
                {" · "}
                {t("shows.duration", { duration: formatDuration(duration) })}
              </>
            )}
          </Text>
        </Stack>
        <Group gap={6} wrap="nowrap">
          {/* `R-SHOW-26a` — so a rehearsal left live is visible from the list. */}
          {show.testRun ? (
            <Badge color="gray" variant="outline" radius="sm">
              {t("shows.testRunBadge")}
            </Badge>
          ) : null}
          <ShowStateBadge state={show.state} />
        </Group>
      </Group>
    </Paper>
  );
}
